import { useEffect, useState, useCallback } from "react";
import API_ROUTES from "~/api-routes";
import useDebounce from "~/hooks/useDebounce";
import apiService from "~/services/service";
import z from "zod";
import { fetchServices } from "~/components/global-entities/globalEntitiesSlice";
import { useDispatch, useSelector } from "react-redux";
import { getStatusOptions, StatusKey } from "~/utils/status";

const useMyProcesses = ({ allEntities }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [totalRecord, setTotalRecords] = useState(0);
  const [sortMeta, setSortMeta] = useState([{ field: "name", order: 1 }]);
  const [accessServices, setAccessServices] = useState([]);
  const [selectedValues, setSelectedValues] = useState({
    status: [],
    service_line: [],
  });
  const [chipData, setChipData] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    status: [],
    service_line: [],
  });
  const { access, role: userRole } = useSelector((state) => state.login);
  const [serviceSubOptions, setServiceSubOptions] = useState([]);
  const [search, setSearch] = useState("");
  const debouncedSearchTerm = useDebounce(search, 500);

  const [filter, setFilter] = useState({
    status: [],
    service_line: [],
  });
  const statusOption = getStatusOptions([
    StatusKey.IN_PROGRESS,
    StatusKey.COMPLETED,
    StatusKey.OVERDUE,
    StatusKey.NOT_STARTED,
  ]);
  const entityservices = useSelector(
    (state) => state.globalEntities.entityservices,
  );
  useEffect(() => {
    if (entityservices) {
      const uniqueServices = [
        ...new Set(access.flatMap((item) => item.services)),
      ];
      const accessService =
        userRole !== "GXM_ADMIN"
          ? entityservices.filter((service) =>
              uniqueServices.includes(service.id),
            )
          : entityservices;

      const serviceSubOptions = accessService?.map((data) => {
        return {
          name: data?.name,
          code: data?.id,
        };
      });
      setServiceSubOptions(serviceSubOptions);
      setAccessServices(accessService);
    }
  }, [entityservices]);

  useEffect(() => {
    dispatch(fetchServices());
  }, []);

  const handleChipData = (selectedValues) => {
    let chipArray = [];
    for (const key in selectedValues) {
      selectedValues[key].map((data) => {
        chipArray.push({ category: key, value: data });
      });
    }
    setChipData(chipArray);
  };
  const fetchData = useCallback(
    async ({
      pageIndex = page,
      pageSize = rows,
      sortBy = sortMeta?.[0]?.field ?? "name",
      sortOrder = sortMeta?.[0]?.order === 1 ? "ASC" : "DESC",
      status = selectedValues?.status?.join(",") || "",
      service = selectedValues?.service_line?.join(",") || "",
      searchValue = search || "",
    } = {}) => {
      setLoading(true);
      setIsDataAvailable(true);

      try {
        const { data } = await apiService(
          API_ROUTES.FETCH_USER_PROCESSES,
          "GET",
          {
            replace: {
              page: pageIndex + 1,
              pageSize,
              sortBy,
              sortOrder,
              status,
              service,
              search: encodeURIComponent(searchValue),
            },
            schema: z.object({
              page: z.number().optional(),
              pageSize: z.number().optional(),
              sortBy: z
                .enum(["name", "entity", "service", "due_date"])
                .optional(),
              sortOrder: z.enum(["ASC", "DESC"]).optional(),
              status: z.string().optional(),
              service: z.string().optional(),
              search: z.string().optional(),
            }),
          },
        );
        setTotalRecords(data?.totalRecords);
        if (data?.records?.length) {
          setData(data?.records);
          setIsDataAvailable(true);
        } else {
          if (searchValue.length > 0) {
            setIsDataAvailable(false);
          } else {
            const hasFilters = status.length > 0 || service.length > 0;
            setIsDataAvailable(hasFilters);
          }
          setData([]);
        }
      } catch (error) {
        setData([]);
        setIsDataAvailable(false);
      } finally {
        setLoading(false);
      }
    },
    [page, rows, sortMeta],
  );

  useEffect(() => {
    fetchData({
      pageIndex: page,
      pageSize: rows,
      service: selectedValues?.service_line?.join(","),
      status: selectedValues?.status?.join(","),
      searchValue: search,
    });
  }, [sortMeta]);

  useEffect(() => {
    setPage(0);
    setRows(10);
    fetchData({
      pageIndex: 0,
      pageSize: 10,
      service: selectedValues?.service_line?.join(","),
      status: selectedValues?.status?.join(","),
      searchValue: debouncedSearchTerm,
    });
  }, [debouncedSearchTerm]);

  const onPageChange = ({ page, rows }) => {
    setPage(page);
    setRows(rows);
    fetchData({
      pageIndex: page,
      pageSize: rows,
      service: filter?.service_line?.join(","),
      status: filter?.status?.join(","),
      searchValue: search,
    });
  };

  const onSort = (e) => {
    setSortMeta(e.multiSortMeta);
  };

  const applyFilters = () => {
    setPage(0);
    setRows(10);
    fetchData({
      pageIndex: 0,
      pageSize: 10,
      service: selectedValues?.service_line?.join(",") || "",
      status: selectedValues?.status?.join(",") || "",
      searchValue: search,
    });
    setFilter(selectedValues);
    handleChipData(selectedValues);
  };

  const deSelectAll = () => {
    setPage(0);
    setRows(10);
    setFilter({
      status: [],
      service_line: [],
    });
    setSelectedFilters({
      status: [],
      service_line: [],
    });
    setSelectedValues({
      status: [],
      service_line: [],
    });
    setChipData([]);
    fetchData({
      pageIndex: 0,
      pageSize: 10,
      service: "",
      status: "",
      searchValue: search,
    });
  };
  const handleRemoveFilter = (data) => {
    const array = selectedValues[data.category]?.filter((value) => {
      return value !== data?.value;
    });

    delete selectedFilters[data?.value];
    setSelectedFilters(selectedFilters);
    const filteredValue = {
      ...selectedValues,
      [data.category]: array,
    };
    setPage(0);
    setRows(10);
    setFilter(filteredValue);
    handleChipData(filteredValue);
    setSelectedValues(filteredValue);
    fetchData({
      pageIndex: 0,
      pageSize: 10,
      service: filteredValue?.service_line?.join(",") || "",
      status: filteredValue?.status?.join(",") || "",
      searchValue: search,
    });
  };
  const getStatus = (code) => {
    return statusOption.find((data) => data?.code === code);
  };
  const getService = (id) => {
    return entityservices?.find((data) => data?.id === id);
  };
  const shouldHideServiceFilter =
    accessServices.length <= 1 && allEntities.length <= 1;

  return {
    data,
    loading,
    isDataAvailable,
    totalRecord,
    onPageChange,
    rows,
    page,
    sortMeta,
    onSort,
    chipData,
    setChipData,
    selectedFilters,
    setSelectedFilters,
    applyFilters,
    selectedValues,
    setSelectedValues,
    deSelectAll,
    serviceSubOptions,
    handleRemoveFilter,
    getStatus,
    statusOption,
    getService,
    shouldHideServiceFilter,
    searchQuery: search,
    setSearchQuery: setSearch,
  };
};

export default useMyProcesses;
